import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { DashboardInfoTypes } from 'features/Codes'
import api from 'api/axios/foka'
import { getPathAdAccountId, serializeError } from 'utils'
import { createAxiosThunk } from 'utils/reduxUtil'
import { getTrackInfo } from 'api/axios/crux/bcon'

const getInitialState = () => ({
  visible:
    JSON.parse(localStorage.getItem('infoVisible')) === null
      ? false
      : JSON.parse(localStorage.getItem('infoVisible')),
  type: DashboardInfoTypes.AD_ACCOUNT,
  id: getPathAdAccountId(),
  isLoading: false,
  loaded: false,
  error: undefined,
  accountError: undefined,
  walletSpendError: undefined,
  totalError: undefined,
  account: {},
  walletSpend: {},
  totalCampaign: {},
  campaign: {
    basic: {},
    trackInfo: {},
    operation: {},
  },
  totalAdGroup: {},
  adGroup: {
    basic: {},
    operation: {},
  },
  totalKeyword: {},
  totalContentMatching: {},
  keyword: {},
  totalCreative: {},
  creative: {},
  tabs: {
    [DashboardInfoTypes.AD_ACCOUNT]: 'priceInfoTab',
    [DashboardInfoTypes.TOTAL_CAMPAIGN]: 'priceInfoTab',
    [DashboardInfoTypes.TOTAL_AD_GROUP]: 'priceInfoTab',
    [DashboardInfoTypes.TOTAL_KEYWORD]: 'priceInfoTab',
    [DashboardInfoTypes.TOTAL_CREATIVE]: 'priceInfoTab',
  },
})

export const fetchDashboardInfoAccount = createAxiosThunk(
  'dashboard/fetchDashboardInfoAccount',
  () => api.dashboard.getAccountOperation()
)

export const fetchDashboardInfoWalletSpend = createAsyncThunk(
  'dashboard/fetchDashboardInfoBizWalletsSpend',
  async (noArgs, { getState, rejectWithValue }) => {
    try {
      const state = getState().account.current?.state || []
      if (state.includes('NOT_CONNECTED_BIZ_WALLET')) {
        return {}
      }
      const { data } = await api.ad.getBizWalletsSpendInfo()
      return data
    } catch (error) {
      if (error && error.response && error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(serializeError(error))
      }
    }
  }
)

export const fetchDashboardInfoCampaign = createAsyncThunk(
  'dashboard/fetchDashboardInfoCampaign',
  async ({ id, showError = false }, { getState, rejectWithValue }) => {
    const adAccountId = getState().account.current.id
    try {
      const {
        data: { campaign },
      } = await api.dashboard.getCampaign(id)
      const trackInfo = campaign.trackId
        ? await getTrackInfo({
            trackId: campaign.trackId,
            adAccountId,
          })
            .then(res => res.data.body)
            .catch(() => undefined)
        : undefined
      const { data: operation } = await api.dashboard.getCampaignOperation(id)
      return {
        basic: campaign,
        trackInfo,
        operation,
      }
    } catch (error) {
      if (error && error.response && error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(serializeError(error))
      }
    }
  }
)

export const fetchDashboardInfoAdGroup = createAsyncThunk(
  'dashboard/fetchDashboardInfoAdGroup',
  async ({ id, showError = false }, { rejectWithValue }) => {
    try {
      const {
        data: { adGroup: basic },
      } = await api.dashboard.getAdGroup(id)
      const { data: operation } = await api.dashboard.getAdGroupOperation(id)
      return {
        basic,
        operation,
      }
    } catch (error) {
      if (error && error.response && error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(serializeError(error))
      }
    }
  }
)

export const fetchDashboardInfoKeyword = createAsyncThunk(
  'dashboard/fetchDashboardInfoKeyword',
  async ({ id, showError = false }, { rejectWithValue }) => {
    try {
      const {
        data: { keyword },
      } = await api.dashboard.getKeyword(id)
      return keyword
    } catch (error) {
      if (error && error.response && error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(serializeError(error))
      }
    }
  }
)

export const fetchDashboardInfoCreative = createAsyncThunk(
  'dashboard/fetchDashboardInfoCreative',
  async ({ id, showError = false }, { rejectWithValue }) => {
    try {
      const {
        data: { creative },
      } = await api.dashboard.getCreative(id)
      return creative
    } catch (error) {
      if (error && error.response && error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(serializeError(error))
      }
    }
  }
)

export const fetchDashboardInfoTotalCampaign = createAsyncThunk(
  'dashboard/fetchDashboardInfoTotalCampaign',
  async (id, { getState }) => {
    const {
      common: { from, to },
      filter: { filters },
    } = getState().dashboard
    const { data: operation } = await api.dashboard.getCampaignFilterOperation({
      from,
      to,
      filters,
    })
    return {
      operation,
    }
  }
)

export const fetchDashboardInfoTotalAdGroup = createAsyncThunk(
  'dashboard/fetchDashboardInfoTotalAdGroup',
  async (id, { getState }) => {
    const {
      common: { from, to },
      filter: { filters },
      table: {
        campaignTable: { selectedIds: campaignIds },
      },
    } = getState().dashboard
    const { data: operation } = await api.dashboard.getAdGroupFilterOperation({
      from,
      to,
      campaignIds,
      filters,
    })
    return {
      operation,
    }
  }
)

export const fetchDashboardInfoTotalKeyword = createAsyncThunk(
  'dashboard/fetchDashboardInfoTotalKeyword',
  async (id, { getState }) => {
    const {
      common: { from, to },
      filter: { filters },
      table: {
        campaignTable: { selectedIds: campaignIds },
        adGroupTable: { selectedIds: adGroupIds },
      },
    } = getState().dashboard
    const { data: operation } = await api.dashboard.getKeywordFilterOperation({
      from,
      to,
      campaignIds,
      adGroupIds,
      filters,
    })
    return {
      operation,
    }
  }
)

export const fetchDashboardInfoTotalCreative = createAsyncThunk(
  'dashboard/fetchDashboardInfoTotalCreative',
  async (id, { getState }) => {
    const {
      common: { from, to },
      filter: { filters },
      table: {
        campaignTable: { selectedIds: campaignIds },
        adGroupTable: { selectedIds: adGroupIds },
      },
    } = getState().dashboard
    const { data: operation } = await api.dashboard.getCreativeFilterOperation({
      from,
      to,
      campaignIds,
      adGroupIds,
      filters,
    })
    return {
      operation,
    }
  }
)

const dashboardInfoSlice = createSlice({
  name: 'dashboard/info',
  initialState: getInitialState(),

  reducers: {
    setVisible: (state, { payload }) => {
      localStorage.setItem('infoVisible', payload)
      state.visible = payload
    },
    setInfo: (state, { payload }) => {
      state.type = payload.type
      state.id = payload.id || payload.creativeId
      state.loaded = false
    },
    setTab: (state, { payload: { type, value } }) => {
      state.tabs[type] = value
    },
    clearError: state => {
      state.error = undefined
    },
    reset: () => getInitialState(),
  },

  extraReducers: builder => {
    builder.addCase(fetchDashboardInfoAccount.pending, state => {
      state.accountError = undefined
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoAccount.fulfilled,
      (state, { payload }) => {
        state.account = payload
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoAccount.rejected,
      (state, { payload }) => {
        state.accountError = payload
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoWalletSpend.pending, state => {
      state.walletSpendError = undefined
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoWalletSpend.fulfilled,
      (state, { payload }) => {
        state.walletSpend = payload
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoWalletSpend.rejected,
      (state, { payload }) => {
        state.walletSpendError = payload
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoCampaign.pending, state => {
      state.loaded = true
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoCampaign.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { id },
          },
        }
      ) => {
        state.campaign = payload
        state.type = DashboardInfoTypes.CAMPAIGN
        state.id = id
        state.loaded = true
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoCampaign.rejected,
      (
        state,
        {
          payload,
          meta: {
            arg: { showError },
          },
        }
      ) => {
        if (showError) {
          state.error = {
            ...payload,
            type: DashboardInfoTypes.CAMPAIGN,
          }
        }
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoAdGroup.pending, state => {
      state.loaded = true
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoAdGroup.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { id },
          },
        }
      ) => {
        state.adGroup = payload
        state.type = DashboardInfoTypes.AD_GROUP
        state.id = id
        state.loaded = true
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoAdGroup.rejected,
      (
        state,
        {
          payload,
          meta: {
            arg: { showError },
          },
        }
      ) => {
        if (showError) {
          state.error = {
            ...payload,
            type: DashboardInfoTypes.AD_GROUP,
          }
        }
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoKeyword.pending, state => {
      state.loaded = true
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoKeyword.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { id },
          },
        }
      ) => {
        state.keyword = payload
        state.type = DashboardInfoTypes.KEYWORD
        state.id = id
        state.loaded = true
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoKeyword.rejected,
      (
        state,
        {
          payload,
          meta: {
            arg: { showError },
          },
        }
      ) => {
        if (showError) {
          state.error = {
            ...payload,
            type: DashboardInfoTypes.KEYWORD,
          }
        }
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoCreative.pending, state => {
      state.loaded = true
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoCreative.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { id },
          },
        }
      ) => {
        state.creative = payload
        state.type = DashboardInfoTypes.CREATIVE
        state.id = id
        state.loaded = true
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoCreative.rejected,
      (
        state,
        {
          payload,
          meta: {
            arg: { showError },
          },
        }
      ) => {
        if (showError) {
          state.error = {
            ...payload,
            type: DashboardInfoTypes.CREATIVE,
          }
        }
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoTotalCampaign.pending, state => {
      state.totalError = undefined
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoTotalCampaign.fulfilled,
      (state, { payload }) => {
        state.totalCampaign = payload
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoTotalCampaign.rejected,
      (state, { payload }) => {
        state.totalError = payload
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoTotalAdGroup.pending, state => {
      state.totalError = undefined
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoTotalAdGroup.fulfilled,
      (state, { payload }) => {
        state.totalAdGroup = payload
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoTotalAdGroup.rejected,
      (state, { payload }) => {
        state.totalError = payload
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoTotalKeyword.pending, state => {
      state.totalError = undefined
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoTotalKeyword.fulfilled,
      (state, { payload }) => {
        state.totalKeyword = payload
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoTotalKeyword.rejected,
      (state, { payload }) => {
        state.totalError = payload
        state.isLoading = false
      }
    )

    builder.addCase(fetchDashboardInfoTotalCreative.pending, state => {
      state.totalError = undefined
      state.isLoading = true
    })

    builder.addCase(
      fetchDashboardInfoTotalCreative.fulfilled,
      (state, { payload }) => {
        state.totalCreative = payload
        state.isLoading = false
      }
    )

    builder.addCase(
      fetchDashboardInfoTotalCreative.rejected,
      (state, { payload }) => {
        state.totalError = payload
        state.isLoading = false
      }
    )
  },
})

const { actions, reducer } = dashboardInfoSlice
export const { setVisible, setInfo, setTab, clearError, reset } = actions
export default reducer
